import { useEffect, useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { computeRepScorecard, TIER_CONFIG, type RepScorecard } from "@/data/commissionEngine";
import { computeRepPerformanceInsights, type RepPerformanceInsight } from "@/lib/repPerformanceIntelligence";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  Users, TrendingUp, DollarSign, Award, Target, BarChart3,
  CheckCircle, AlertTriangle, Percent, Zap, MapPin,
  CreditCard, Banknote, ShieldCheck, Info,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";

function pct(v: number): string { return `${Math.round(v * 100)}%`; }
function currency(v: number): string { return `$${Math.round(v).toLocaleString()}`; }

function MetricCard({ label, value, sub, icon: Icon, color }: { label: string; value: string | number; sub?: string; icon: React.ElementType; color: string }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-card">
      <div className={cn("p-2 rounded-md", color.replace("text-", "bg-").replace(/(?<=bg-)\w+/, m => m + "/10"))}>
        <Icon className={cn("h-4 w-4", color)} />
      </div>
      <div>
        <p className="text-sm font-bold">{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
        {sub && <p className="text-[9px] text-muted-foreground/70">{sub}</p>}
      </div>
    </div>
  );
}

function ScorecardDetail({ scorecard, perfInsight }: { scorecard: RepScorecard; perfInsight?: RepPerformanceInsight }) {
  const tier = TIER_CONFIG[scorecard.performanceTier];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-bold text-primary">{scorecard.repName.split(" ").map(n => n[0]).join("")}</span>
          </div>
          <div>
            <h3 className="text-base font-bold">{scorecard.repName}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Badge variant="outline" className={cn("text-[10px]", tier.color, tier.bg)}>{tier.label} Performer</Badge>
              {perfInsight && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                  <MapPin className="h-2.5 w-2.5 mr-0.5" /> {perfInsight.region}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-primary">{pct(scorecard.closeRate)}</p>
          <p className="text-[10px] text-muted-foreground">Close Rate</p>
        </div>
      </div>

      <Separator />

      {/* ── Opportunity-Aware Intelligence ── */}
      {perfInsight && (
        <div className="space-y-3">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="h-3 w-3 text-primary" /> Opportunity Intelligence
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border p-2.5 text-center">
              <p className="text-[18px] font-bold text-foreground">{perfInsight.netConversion}%</p>
              <p className="text-[9px] text-muted-foreground font-medium mt-0.5">Net Conversion</p>
              <p className="text-[8px] text-muted-foreground/60">{perfInsight.totalLeadsAssigned} leads assigned</p>
            </div>
            <div className="rounded-lg border p-2.5 text-center">
              <p className={cn("text-[18px] font-bold", perfInsight.highFitConversion >= 50 ? "text-success" : perfInsight.highFitConversion >= 35 ? "text-warning" : "text-destructive")}>
                {perfInsight.highFitConversion}%
              </p>
              <p className="text-[9px] text-muted-foreground font-medium mt-0.5">High-Fit Conversion</p>
              <p className="text-[8px] text-muted-foreground/60">{perfInsight.highFitLeads} high-fit leads</p>
            </div>
            <div className="rounded-lg border p-2.5 text-center">
              <p className="text-[18px] font-bold text-foreground">${perfInsight.revenuePerLead.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground font-medium mt-0.5">Revenue / Lead</p>
              <p className="text-[8px] text-muted-foreground/60">
                Avg quality: <span className={cn("font-medium",
                  perfInsight.avgLeadQuality === "High" ? "text-success" :
                  perfInsight.avgLeadQuality === "Medium" ? "text-warning" : "text-destructive"
                )}>{perfInsight.avgLeadQuality}</span>
              </p>
            </div>
          </div>
          {/* Insight */}
          <div className={cn(
            "rounded-lg border px-3 py-2.5 flex items-start gap-2",
            perfInsight.insightSeverity === "positive" ? "bg-success/5 border-success/15" :
            perfInsight.insightSeverity === "caution" ? "bg-warning/5 border-warning/15" :
            "bg-muted/30 border-border",
          )}>
            {perfInsight.insightSeverity === "positive" ? <CheckCircle className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" /> :
             perfInsight.insightSeverity === "caution" ? <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" /> :
             <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />}
            <div>
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">Performance Insight</p>
              <p className="text-[12px] font-medium leading-snug">{perfInsight.insight}</p>
            </div>
          </div>
        </div>
      )}

      {perfInsight && <Separator />}

      {/* Performance Metrics */}
      <div>
        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Performance</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <MetricCard label="Appointments Ran" value={scorecard.appointmentsRan} icon={Target} color="text-primary" />
          <MetricCard label="Demos Completed" value={scorecard.demosCompleted} icon={CheckCircle} color="text-success" />
          <MetricCard label="No Demos" value={scorecard.noDemos} icon={AlertTriangle} color="text-muted-foreground" />
          <MetricCard label="Close Rate" value={pct(scorecard.closeRate)} sub="Sold / Demos" icon={TrendingUp} color="text-success" />
          <MetricCard label="Demo to Sale" value={pct(scorecard.demoToSaleRate)} icon={Percent} color="text-primary" />
          <MetricCard label="Appt to Sale" value={pct(scorecard.appointmentToSaleRate)} icon={BarChart3} color="text-warning" />
        </div>
      </div>

      {/* Financial Metrics (non-payout) */}
      <div>
        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Financial</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <MetricCard label="Revenue Sold" value={currency(scorecard.totalRevenueSold)} icon={DollarSign} color="text-success" />
          <MetricCard label="Average Ticket" value={currency(scorecard.averageTicket)} icon={Banknote} color="text-primary" />
          <MetricCard label="Financing Mix" value={pct(scorecard.financingMix)} sub={`${scorecard.financingCount} financed / ${scorecard.cashCount} cash`} icon={CreditCard} color="text-warning" />
        </div>
      </div>

      {/* Discipline Metrics */}
      <div>
        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Discipline</p>
        <div className="space-y-2">
          {[
            { label: "Result Submission Rate", value: scorecard.resultSubmissionRate },
            { label: "Follow-Up Completion", value: scorecard.followUpCompletionRate },
            { label: "Reschedule Recovery", value: scorecard.rescheduleRecoveryRate },
          ].map(m => (
            <div key={m.label} className="flex items-center gap-3">
              <p className="text-xs text-muted-foreground w-44">{m.label}</p>
              <Progress value={m.value * 100} className="flex-1 h-2" />
              <p className={cn("text-xs font-bold w-12 text-right", m.value >= 0.9 ? "text-success" : m.value >= 0.7 ? "text-warning" : "text-destructive")}>{pct(m.value)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function RepScorecards() {
  const { appointmentResults, followUpTasks, jobs, salesOpportunities, quotes, leads } = useAppData();
  const [selectedRep, setSelectedRep] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState("all");
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          [
            ...salesOpportunities.map((o) => String(o.assignedRep ?? "").trim()),
            ...quotes.map((q) => String((q as { createdBy?: string }).createdBy ?? "").trim()),
            ...appointmentResults.map((r) => String(r.assignedRep ?? "").trim()),
            ...followUpTasks.map((t) => String(t.assignedTo ?? "").trim()),
          ].filter((v) => isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }

      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);
      if (cancelled) return;

      const map = new Map<string, string>();
      for (const row of [...(byIdRes.data ?? []), ...(byAuthIdRes.data ?? [])]) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const full = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        if (!full) continue;
        const id = String(row.id ?? "").trim();
        const authId = String(row.auth_user_id ?? "").trim();
        if (id) map.set(id, full);
        if (authId) map.set(authId, full);
      }
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [appointmentResults, followUpTasks, salesOpportunities, quotes]);

  const resolveRep = (value: string | null | undefined): string =>
    String(resolveProfileDisplayName(value, repNameById, "Unassigned") ?? "Unassigned").trim() || "Unassigned";

  const isMissingRep = (value: string | null | undefined): boolean => {
    const normalized = String(value ?? "").trim().toLowerCase();
    return (
      normalized.length === 0 ||
      normalized === "unassigned" ||
      normalized === "unknown" ||
      normalized === "n/a" ||
      normalized === "na" ||
      normalized === "null" ||
      normalized === "undefined" ||
      normalized === "—" ||
      normalized === "-"
    );
  };

  const leadRepByLeadId = useMemo(() => {
    const map = new Map<string, string>();
    for (const opp of salesOpportunities) {
      const rep = resolveRep(opp.assignedRep);
      const leadId = String(opp.leadId ?? "").trim();
      if (leadId) map.set(leadId, rep);
      const oppId = String(opp.id ?? "").trim();
      if (oppId) map.set(oppId, rep);
    }
    return map;
  }, [salesOpportunities, repNameById]);

  const leadAssignedRepById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of leads) {
      const id = String(l.id ?? "").trim();
      if (!id) continue;
      map.set(id, resolveRep(String((l as { assignedRep?: string }).assignedRep ?? "").trim()));
    }
    return map;
  }, [leads, repNameById]);

  const quoteRepByLeadId = useMemo(() => {
    const map = new Map<string, { rep: string; ts: number }>();
    const parseTs = (value: string | undefined | null): number => Date.parse(String(value ?? "")) || 0;
    for (const q of quotes) {
      const leadId = String((q as { leadId?: string; lead_id?: string }).leadId ?? (q as { lead_id?: string }).lead_id ?? "").trim();
      if (!leadId) continue;
      const rep = resolveRep((q as { createdBy?: string }).createdBy);
      const ts = parseTs(
        String(
          (q as { updatedAt?: string; updated_at?: string; createdAt?: string; created_at?: string }).updatedAt ??
          (q as { updated_at?: string }).updated_at ??
          (q as { createdAt?: string }).createdAt ??
          (q as { created_at?: string }).created_at ??
          "",
        ),
      );
      const prev = map.get(leadId);
      if (!prev || ts >= prev.ts) map.set(leadId, { rep, ts });
    }
    return map;
  }, [quotes, repNameById]);

  const normalizedResults = useMemo(() => {
    return appointmentResults.map((r) => {
      const leadId = String(r.leadId ?? "").trim();
      const oppId = String(r.opportunityId ?? "").trim();
      const leadRep = leadAssignedRepById.get(leadId);
      const quoteRep = quoteRepByLeadId.get(leadId)?.rep;
      const oppRep = leadRepByLeadId.get(leadId) ?? leadRepByLeadId.get(oppId);
      const resolvedRep = !isMissingRep(leadRep)
        ? leadRep
        : !isMissingRep(quoteRep)
          ? quoteRep
          : !isMissingRep(oppRep)
            ? oppRep
            : undefined;
      return {
        ...r,
        assignedRep: resolvedRep ?? resolveRep(r.assignedRep),
      };
    });
  }, [appointmentResults, quoteRepByLeadId, leadRepByLeadId, leadAssignedRepById, repNameById]);

  const reps = useMemo((): string[] =>
    [...new Set(normalizedResults.map((r) => r.assignedRep).filter(Boolean))].sort(),
    [normalizedResults]
  );

  const scorecards = useMemo((): RepScorecard[] => {
    return reps.map(rep => {
      const repTasks = followUpTasks.filter((t) => resolveRep(t.assignedTo) === rep);
      const completedTasks = repTasks.filter(t => t.status === "completed");
      const followUpRate = repTasks.length > 0 ? completedTasks.length / repTasks.length : 1.0;
      return computeRepScorecard(rep, normalizedResults, followUpRate, jobs);
    });
  }, [reps, normalizedResults, followUpTasks, jobs, repNameById]);

  const perfInsights = useMemo(() => computeRepPerformanceInsights(), []);

  const filtered = useMemo(() => {
    if (tierFilter === "all") return scorecards;
    return scorecards.filter(s => s.performanceTier === tierFilter);
  }, [scorecards, tierFilter]);

  const totals = useMemo(() => ({
    revenue: scorecards.reduce((s, sc) => s + sc.totalRevenueSold, 0),
    deals: scorecards.reduce((s, sc) => s + sc.salesClosed, 0),
    avgClose: scorecards.length > 0 ? scorecards.reduce((s, sc) => s + sc.closeRate, 0) / scorecards.length : 0,
    avgTicket: scorecards.length > 0 ? scorecards.reduce((s, sc) => s + sc.averageTicket, 0) / scorecards.length : 0,
  }), [scorecards]);

  const activeScorecard = selectedRep ? scorecards.find(s => s.repName === selectedRep) : null;
  const activeInsight = selectedRep ? perfInsights.find(i => i.repName === selectedRep) : undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Rep Scorecards
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Performance and discipline metrics powered by Appointment Results
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] gap-1">
          <ShieldCheck className="h-3 w-3" /> Immutable Data Source
        </Badge>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {([
          { label: "Total Revenue Sold", value: currency(totals.revenue), icon: DollarSign, color: "text-success" },
          { label: "Total Deals Closed", value: totals.deals, icon: CheckCircle, color: "text-success" },
          { label: "Avg Close Rate", value: pct(totals.avgClose), icon: TrendingUp, color: "text-warning" },
          { label: "Avg Ticket", value: currency(totals.avgTicket), icon: Banknote, color: "text-primary" },
        ] as const).map(kpi => (
          <Card key={kpi.label} className="border-border/50">
            <CardContent className="p-3 flex items-center gap-3">
              <kpi.icon className={cn("h-5 w-5", kpi.color)} />
              <div>
                <p className="text-lg font-bold">{kpi.value}</p>
                <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder="All Tiers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All Tiers</SelectItem>
            <SelectItem value="elite" className="text-xs">Elite</SelectItem>
            <SelectItem value="strong" className="text-xs">Strong</SelectItem>
            <SelectItem value="solid" className="text-xs">Solid</SelectItem>
            <SelectItem value="developing" className="text-xs">Developing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Rep List */}
        <div className="space-y-2">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Reps ({filtered.length})</p>
          {filtered.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                No appointment results recorded yet. Results will appear after reps submit appointment outcomes.
              </CardContent>
            </Card>
          ) : (
            filtered.map(sc => {
              const tier = TIER_CONFIG[sc.performanceTier];
              const insight = perfInsights.find(i => i.repName === sc.repName);
              return (
                <button
                  key={sc.repName}
                  onClick={() => setSelectedRep(sc.repName)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg border transition-all",
                    selectedRep === sc.repName
                      ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20"
                      : "border-border/50 bg-card hover:border-border"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-primary">{sc.repName.split(" ").map(n => n[0]).join("")}</span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold">{sc.repName}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Badge variant="outline" className={cn("text-[9px] h-4", tier.color, tier.bg)}>{tier.label}</Badge>
                          {insight && (
                            <Badge variant="outline" className={cn("text-[8px] h-4 px-1",
                              insight.insightSeverity === "positive" ? "bg-success/10 text-success border-success/20" :
                              insight.insightSeverity === "caution" ? "bg-warning/10 text-warning border-warning/20" :
                              "bg-muted text-muted-foreground",
                            )}>
                              {insight.highFitConversion}% HF
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-primary">{pct(sc.closeRate)}</p>
                      <p className="text-[9px] text-muted-foreground">{sc.salesClosed} sold</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                    <span>{sc.appointmentsRan} appts</span>
                    <span>{sc.demosCompleted} demos</span>
                    <span>{currency(sc.totalRevenueSold)} rev</span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Detail View */}
        <div className="lg:col-span-2">
          {activeScorecard ? (
            <Card className="border-border/50">
              <CardContent className="p-5">
                <ScorecardDetail scorecard={activeScorecard} perfInsight={activeInsight} />
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/50">
              <CardContent className="p-12 text-center text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium">Select a rep to view their scorecard</p>
                <p className="text-xs mt-1">Performance, financial, and discipline metrics</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
